import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Droplets, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await signup(form.name, form.email, form.password);
      toast.success("Account created!");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#F3F4F1]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#064E3B] flex items-center justify-center">
              <Droplets className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-lg font-semibold">IV Therapy Finder</span>
          </div>
          <h1 className="font-display text-3xl font-semibold text-stone-900">Create your account</h1>
          <p className="text-stone-600 mt-1 text-sm">Free forever. Write reviews and save your favorite providers.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-stone-200 p-7 space-y-4 shadow-sm">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Full name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Password</Label>
            <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password" />
            <p className="text-xs text-stone-500 mt-1">At least 6 characters</p>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#064E3B] hover:bg-[#053d2f]" data-testid="signup-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
          </Button>
          <p className="text-center text-sm text-stone-600 pt-2">
            Already have an account? <Link to="/login" className="text-[#064E3B] font-semibold hover:underline">Sign in →</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
